import React, { useEffect, useState } from 'react';
import activitychaticon from '../../../assets/images/activitychaticon.svg';
import './activitiestab.css';
import TaskAction from '../../../interfaces/task/taskActions/TaskAction';
import { useAuth } from '../../../firebase/AuthProvider';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchLatestTasksForUser } from '../../../services/taskServices/TaskService';
import Task from '../../../interfaces/task/Task';
import { fetchLatestActivityForUser } from '../../../services/taskServices/TaskActionService';
import { getAllUsersInOrganization } from '../../../services/userServices/UserInfoService';
import UserInfo from '../../../interfaces/UserInfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleUser } from '@fortawesome/free-solid-svg-icons';
import { getTimeAgo } from '../../../utilities/dateUtils';

const parseTaskActionDescription = (
    activity: TaskAction,
    users: UserInfo[] | null,
    navigate: (path: string) => void
): string | JSX.Element => {
    const { actionSubType, description, author } = activity;

    const renderDescription = (chat: TaskAction) => {
        if (!chat.description) {
            return null;
        }

        let parts = chat.description.split(/({user:[^}]+}|\n)/g);

        return parts.map((part, i) => {
            if (part === "\n") {
                return <br key={i} />;
            }
            const match = part.match(/{user:(.+)}/);
            if (match) {
                const userId = match[1];
                const mentionedUser = users?.find((user) => user.id === userId);

                if (mentionedUser) {
                    return (
                        <span key={i}
                            className=""
                            onClick={() => navigate(`/profile/user/${mentionedUser.id}`)}
                        >
                            {/* <FontAwesomeIcon icon={faCircleUser} className="mr-1" /> */}
                            {mentionedUser.displayName}
                        </span>
                    );
                } else {
                    return `{Unknown User}`;
                }
            } else {
                return part;
            }
        });
    };

    switch (actionSubType.actionSubTypeName) {
        case "COMMENT":
            const commenter = users?.find((user) => user.id === author?.id);
            const commentText = description || "No comment provided.";
            return (
                <>
                    {commenter ? (
                        <h3>{commenter.displayName} <span>{" "}(commented on task){" "}</span></h3>
                    ) : (
                        "Someone"
                    )}

                    <p
                        onClick={() =>
                            navigate(
                                `/task/${commenter?.organization.abbreviation}-${activity.taskId}`
                            )
                        }
                    >
                        {commenter?.organization.abbreviation + "-" + activity.taskId}
                        : <span>"{renderDescription(activity)}"</span>
                    </p>

                </>
            );

        case "TASK COMPLETED":
            const completer = users?.find((user) => user.id === author?.id);
            return (
                <>
                    {completer ? (
                        <h3>{completer.displayName}<span>{" "}completed the task{" "}</span></h3>
                    ) : (
                        "Someone"
                    )}
                    <p>
                        {completer?.organization.abbreviation + "-" + activity.taskId}
                    </p>
                    .
                </>
            );

        case "FILE UPLOAD":
            const uploader = users?.find((user) => user.id === author?.id);
            return (
                <>
                    {uploader ? (
                        <h3>{uploader.displayName} <span>{" "}uploaded a file to task{" "}</span></h3>
                    ) : (
                        "Someone"
                    )}
                    <p>
                        {uploader?.organization.abbreviation + "-" + activity.taskId} <span>.</span>
                    </p>

                </>
            );

        case "TASK APPROVAL":
            const approver = users?.find((user) => user.id === author?.id);
            return (
                <>
                    {approver ? (
                        <h3>{approver.displayName}<span>{" "}approved the task{" "}</span></h3>
                    ) : (
                        "Someone"
                    )}
                    <p className="font-bold">
                        {approver?.organization.abbreviation + "-" + activity.taskId} <span>.</span>
                    </p>
                </>
            );

        case "TASK REJECTED":
            const rejecter = users?.find((user) => user.id === author?.id);
            return (
                <>
                    {rejecter ? (
                        <h3>{rejecter.displayName}{" "}rejected the task{" "}</h3>
                    ) : (
                        "Someone"
                    )}
                    <p>
                        {rejecter?.organization.abbreviation + "-" + activity.taskId} <span>.</span>
                    </p>

                </>
            );

        case "TASK SUBMISSION":
            const submitter = users?.find((user) => user.id === author?.id);
            return (
                <>
                    {submitter ? (
                        <h3>{submitter.displayName} <span>{" "}submitted the task{" "}</span></h3>
                    ) : (
                        "Someone"
                    )}
                    <p
                        onClick={() =>
                            navigate(
                                `/task/${submitter?.organization.abbreviation}-${activity.taskId}`
                            )
                        }
                    >
                        {submitter?.organization.abbreviation + "-" + activity.taskId} <span>.</span>
                    </p>

                </>
            );

        case "TASK FILE DELETED":
            const deleter = users?.find((user) => user.id === author?.id);
            return (
                <>
                    {deleter ? (
                        <h3>{deleter.displayName} <span>{" "}deleted a file from task{" "}</span></h3>
                    ) : (
                        "Someone"
                    )}
                    <p>
                        {deleter?.organization.abbreviation + "-" + activity.taskId} <span>.</span>
                    </p>

                </>
            );

        case "Email":
            const emailSender = users?.find((user) => user.id === author?.id);
            return (
                <>
                    {emailSender ? (
                        <h3>{emailSender.displayName}<span>{" "}sent an email related to task{" "}</span></h3>
                    ) : (
                        "Someone"
                    )}
                    <p>
                        {emailSender?.organization.abbreviation + "-" + activity.taskId} <span>.</span>
                    </p>

                </>
            );

        case "Phone Call":
            const caller = users?.find((user) => user.id === author?.id);
            return (
                <>
                    {caller ? (
                        <h3>{caller.displayName} <span>{" "}made a phone call related to task{" "}</span></h3>
                    ) : (
                        "Someone"
                    )}
                    <p>
                        {caller?.organization.abbreviation + "-" + activity.taskId} <span>.</span>
                    </p>

                </>
            );

        default:
            return description || "No additional details available.";
    }
};

const Activitiestab: React.FC = () => {
    const { currentUser } = useAuth();
    const { userId } = useParams();
    const navigate = useNavigate();

    const [latestTasks, setLatestTasks] = useState<Task[]>([]);
    const [latestActivity, setLatestActivity] = useState<TaskAction[]>([]);
    const [users, setUsers] = useState<UserInfo[] | null>([]);

    useEffect(() => {
        if (currentUser) {
            const fetchData = async () => {
                const authToken = await currentUser.getIdToken();
                const data = await fetchLatestTasksForUser(authToken, userId);
                if (data) {
                    setLatestTasks(data);
                }
            };

            const fetchActivity = async () => {
                const authToken = await currentUser.getIdToken();
                const data = await fetchLatestActivityForUser(authToken, userId);
                if (data) {
                    setLatestActivity(data);
                }
            };

            const fetchUsers = async () => {
                if (currentUser) {
                    const authToken = await currentUser.getIdToken();
                    const fetchedUsers = await getAllUsersInOrganization(authToken);
                    setUsers(fetchedUsers);
                }
            };

            fetchData();
            fetchActivity();
            fetchUsers();
        }
    }, [currentUser, userId]);

    const navigateFunction = (path: string) => {
        navigate(path);
    };

    return (
        <>
            <div className='common_content_inner mby_t_24'>
                <div className="top border_tittle">
                    <div className="top_bar_tittle">
                        <h2>Latest Activity</h2>
                    </div>
                </div>

                {latestActivity.length > 0 ? (
                    <div className="avtivity_grid">
                        {latestActivity.map((activity, index) => (
                            <div className="avtivity_card" key={index}>
                                <div className="avtivity_card_lft">
                                    <div className="white_circle_icon white_circle_icon_lg flex-shrink-0">
                                        <img style={{ width: '1.5rem' }} src={activitychaticon} alt="activitychaticon" />
                                    </div>
                                    <div className="avtivity_card_cntnt">
                                        <div className="activity-description">
                                            {parseTaskActionDescription(
                                                activity,
                                                users,
                                                navigateFunction
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="avtivity_card_rght">
                                    <div className="avtivity_time">
                                        <p>{getTimeAgo(activity.createDate)}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex justify-center text-sm">
                        <p>No activity found!</p>
                    </div>
                )}

            </div>
        </>
    );
};

export default Activitiestab;